// BL-2282 (PRJ-89) — the one auction status KEY and its display: the dropdown
// row key, the auctions-list Status facet value and the badge input.

import {
  AUCTION_PHASE_KINDS,
  closeStageLabel,
  isCloseStagePosition,
  type AuctionPhaseKind,
  type CloseStage,
  type CloseStageLabelSource,
  type CloseStagePosition,
} from '@auctogon/core/isomorphic/lifecycle';
import type { BadgeTone } from '@auctogon/ui/types';
import { AUCTION_STATUS_TONE } from '../constants/status-legend';

/** A closed auction at a named closing stage (1–4). Stage 0 is plain `closed`. */
export type CloseStageStatusKey = `closed-stage-${CloseStagePosition}`;

/**
 * An auction's status as staff see it: its derived phase, or — for a closed
 * auction past stage 0 — `closed-stage-n`. Kebab so it doubles as a testid
 * suffix (`auction-status-closed-stage-2`) and a URL value.
 */
export type AuctionStatusKey = AuctionPhaseKind | CloseStageStatusKey;

/** The pill an {@link AuctionStatusKey} renders as. */
export interface AuctionStatusDisplay {
  label: string;
  tone: BadgeTone;
  dot: boolean | undefined;
  pulse: boolean | undefined;
}

const CLOSE_STAGE_KEY = /^closed-stage-(\d)$/;

/**
 * The status key for an auction's phase and closing stage.
 *
 * @param phase - The auction's derived phase.
 * @param closeStage - Its derived closing stage (`deriveCloseStage`); only read while closed.
 * @returns `closed-stage-n` for a closed auction at stage n > 0, else the phase.
 */
export function auctionStatusKey(phase: AuctionPhaseKind, closeStage: number): AuctionStatusKey {
  if (phase !== 'closed' || !isCloseStagePosition(closeStage)) return phase;
  return `closed-stage-${closeStage}`;
}

/**
 * Parse a status key (a URL facet value, a menu row key) back into its phase and stage.
 *
 * @param raw - A candidate key.
 * @returns `{ phase, closeStage }`, or `null` for anything outside the vocabulary
 *   (`closed-stage-0` is spelled `closed`; there is no stage 5).
 */
export function parseAuctionStatusKey(raw: string): { phase: AuctionPhaseKind; closeStage: CloseStage } | null {
  if ((AUCTION_PHASE_KINDS as readonly string[]).includes(raw)) {
    return { phase: raw as AuctionPhaseKind, closeStage: 0 };
  }
  const match = CLOSE_STAGE_KEY.exec(raw);
  const stage = match ? Number(match[1]) : NaN;
  return isCloseStagePosition(stage) ? { phase: 'closed', closeStage: stage } : null;
}

/**
 * How a status key renders: the legend's label, tone, dot and pulse, except
 * that `closed` and every stage key read the house's words, on the closed tone
 * (no second colour map).
 *
 * @param key - The status key.
 * @param labels - The house's closing-stage labels.
 * @returns The pill's label, tone, dot and pulse.
 */
export function auctionStatusDisplay(key: AuctionStatusKey, labels: CloseStageLabelSource): AuctionStatusDisplay {
  const parsed = parseAuctionStatusKey(key);
  // A value outside the vocabulary (a stale wire token) renders as itself, on
  // the neutral tone — the badge's long-standing fallback.
  if (!parsed) return { label: key, tone: 'neutral', dot: undefined, pulse: undefined };
  const legend = AUCTION_STATUS_TONE[parsed.phase];
  const label = parsed.phase === 'closed' ? closeStageLabel(parsed.closeStage, labels) : legend.label;
  return { label, tone: legend.tone, dot: legend.dot, pulse: legend.pulse };
}

/**
 * One status key per stage the house names, in order — the stage rows in the
 * status menu and the stage options in the auctions-list Status facet.
 *
 * @param labels - The house's closing-stage labels.
 * @returns `closed-stage-1` … `closed-stage-n`; empty when the house names none.
 */
export function closeStageStatusKeys(labels: CloseStageLabelSource): CloseStageStatusKey[] {
  return labels.stages
    .map((_, i) => i + 1)
    .filter(isCloseStagePosition)
    .map((stage) => `closed-stage-${stage}` as const);
}
